import { ExclamationCircleFilled } from '@ant-design/icons';
import { Card, Descriptions, Empty, Input, Modal, Progress, Slider, Spin, Switch } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import NavBar from '../../../../components/NavBar';
import SideBar from '../../../../components/SideBar';
import Footer from '../../../../components/Footer';
import { rightsService } from '../../../../services/rightsService';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { frenchDate } from '../../../../variables/functions';
import { LOGGED_USER } from '../../../../variables/constants';

const { confirm } = Modal;

const DetailsLotAttestationsDispatched = () => {
  const [lots, setLots] = useState([]);
  const [agences, setAgences] = useState([]);
  const [courtiers, setCourtiers] = useState([]);
  const [intermediaires, setIntermediaires] = useState([]);
  const [lotAttestation, setLotAttestation] = useState("");
  const [compagnie, setCompagnie] = useState("");
  const [affectationModalOpen, setAffectationModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [firstAttestation, setFirstAttestation] = useState(0);
  const [lastAttestation, setLastAttestation] = useState(0);
  const [nombre, setNombre] = useState(0);

  const [reload, setReload] = useState(false);
  const [selectedAgence, setSelectedAgence] = useState("");
  const [getLotLoading, setGetLotLoading] = useState(false);

  // Nouveaux états pour validation temps réel
  const [errors, setErrors] = useState({ first: '', range: '' });
  const [isValid, setIsValid] = useState(false);

  const param = useParams();
  const navigate = useNavigate();

  // Le Pool consulte cette vue en lecture seule : aucune action
  // (Valider réception / Annuler dispatch / Dispatcher) ne lui est proposée.
  const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

  useEffect(() => {
    setGetLotLoading(true);
    axiosInstance
      .get(`api/lot-attestation/${param.lotAttestationId}/`)
      .then((res) => {
        console.log(res);
        // On masque les dispatchs au lot vide (plage absente) : sinon la carte afficherait
        // une plage « - » et un cercle « NaN% » (division par l.lot.length = 0).
        setLots((res.data.dispatchedLots || []).filter((l) => Array.isArray(l?.lot) && l.lot.length > 0));
        setLotAttestation(res.data);
        setCompagnie(res.data.compagnie);
        setReload(false);

        axiosInstance
          .get(`api/agence/organisme/code/${res.data?.compagnie?.code}`)
          .then((resp) => {
            setGetLotLoading(false);
            setAgences(resp.data);
            setSelectedAgence(resp.data[0]?.codeAgence || "");
          })
          .catch((err) => {
            console.error(err);
            setGetLotLoading(false);
          });

        axiosInstance
          .get(`api/agence/organisme/courtiers/${res.data?.compagnie?.code}`)
          .then((resp) => {
            setGetLotLoading(false);
            setCourtiers(resp.data);
          })
          .catch((err) => {
            console.error(err);
            setGetLotLoading(false);
          });
      })
      .catch((err) => {
        console.error(err);
        setGetLotLoading(false);
      });
  }, [reload]);

  // Ensemble des numéros disponibles (encore dispatchables) dans CE lot
  const availableSet = useMemo(
    () => new Set(lotAttestation?.updatedLot || []),
    [lotAttestation?.updatedLot]
  );

  // Groupes continus de numéros disponibles
  const availableGroups = useMemo(() => {
    const arr = lotAttestation?.updatedLot || [];
    if (!arr.length) return [];
    const res = [];
    let cur = [arr[0]];
    for (let i = 1; i < arr.length; i++) {
      if (arr[i] === arr[i - 1] + 1) cur.push(arr[i]);
      else {
        res.push(cur);
        cur = [arr[i]];
      }
    }
    res.push(cur);
    return res;
  }, [lotAttestation?.updatedLot]);

  const findGroupFor = (n) =>
    availableGroups.find((g) => n >= g[0] && n <= g[g.length - 1]);

  // --- Validations live ---
  const validateFirst = (n) => {
    if (!n || Number.isNaN(n)) {
      setErrors((e) => ({ ...e, first: "Renseignez un numéro." }));
      return false;
    }
    if (!availableSet.has(n)) {
      setErrors((e) => ({
        ...e,
        first: "Numéro inexistant dans le lot courant ou déjà utilisé.",
      }));
      return false;
    }
    setErrors((e) => ({ ...e, first: "" }));
    return true;
  };

  const validateRange = (first, last, nb) => {
    if (!validateFirst(first)) return false;

    const end = nb ? first + Number(nb) - 1 : Number(last);

    if (!end || end < first) {
      setErrors((e) => ({ ...e, range: "Plage invalide." }));
      return false;
    }

    const group = findGroupFor(first);
    if (!group) {
      setErrors((e) => ({
        ...e,
        range: "Le premier numéro n'appartient à aucune plage disponible.",
      }));
      return false;
    }

    const groupEnd = group[group.length - 1];
    if (end > groupEnd) {
      setErrors((e) => ({
        ...e,
        range: `La plage traverse une rupture. Fin max possible: ${groupEnd}.`,
      }));
      return false;
    }

    for (let v = first; v <= end; v++) {
      if (!availableSet.has(v)) {
        setErrors((e) => ({ ...e, range: `Le numéro ${v} n'est pas disponible.` }));
        return false;
      }
    }

    setErrors((e) => ({ ...e, range: "" }));
    return true;
  };

  // Recalculer la validité globale lors des changements
  useEffect(() => {
    const ok = validateRange(firstAttestation, lastAttestation, nombre);
    setIsValid(ok);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstAttestation, lastAttestation, nombre, lotAttestation?.updatedLot]);

  // (Optionnel) Vérification asynchrone “globale” d’un doublon côté serveur
  // Décommente si ton API expose cette route:
  // GET /api/attestation/check/:numero => { exists: boolean }
  useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      if (!firstAttestation) return;
      // ↓ Décommente ce bloc si la route existe
      // axiosInstance
      //   .get(`/api/attestation/check/${firstAttestation}`, { signal: ctrl.signal })
      //   .then((res) => {
      //     if (res.data?.exists) {
      //       setErrors((e) => ({
      //         ...e,
      //         first: "Ce numéro est déjà utilisé (global).",
      //       }));
      //       setIsValid(false);
      //       toast("Numéro déjà utilisé.");
      //     }
      //   })
      //   .catch(() => {});
    }, 300);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [firstAttestation]);

  const onSubmit = () => {
    if (!isValid) {
      toast("Veuillez corriger les erreurs avant de valider.");
      return;
    }

    setLoading(true);

    const expectedNombre = lastAttestation - firstAttestation + 1;
    const nombreOk = nombre ? nombre === expectedNombre : true;

    if (!nombreOk) {
      setLoading(false);
      toast("Le nombre ne correspond pas à la plage saisie.");
      return;
    }

    const allWantedAttestations = Array.from(
      { length: expectedNombre },
      (_, i) => firstAttestation + i
    );
    const absentElement = allWantedAttestations.some(
      (element) => !lotAttestation?.updatedLot?.includes(element)
    );

    if (absentElement) {
      setLoading(false);
      toast("Il existe des attestations déjà dispatchées ou utilisées dans votre sélection.");
      return;
    }
    setGetLotLoading(true);
    axiosInstance
      .get(
        `/api/dispatched-lot-attestation/lot/${param.lotAttestationId}/${firstAttestation}/${lastAttestation}/${selectedAgence}`
      )
      .then((res) => {
        if (res.status === 226) {
          toast("Ce numéro d'attestation existe déjà!");
        } else {
          setFirstAttestation(0);
          setLastAttestation(0);
          setNombre(0);
          setSelectedAgence(agences[0]?.codeAgence || "");
          setAffectationModalOpen(false);
          // navigate(`/mes-attestations`, { replace: true });
          navigate(`/dispatching-lot-attestation/${res.data.codeId}`, { replace: true });
          window.location.reload();
          toast("Lot d'attestation dispatché avec succès");
        }
        setGetLotLoading(false);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast("Erreur lors de l'ajout du lot d'attestation");
        setLoading(false);
      });
  };

  const showReceptionAttestationConfirm = (lotA) => {
    confirm({
      title: "Etes-vous sûr de vouloir valider la réception du lot dispatché?",
      icon: <ExclamationCircleFilled />,
      content: "Toutes vos actions sont historisées",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        validationReception(lotA);
      },
    });
  };

  const showAnnulationAttestationConfirm = (lotA) => {
    confirm({
      title: "Etes-vous sûr de vouloir annuler le dispatching du lot?",
      icon: <ExclamationCircleFilled />,
      content: "Toutes vos actions sont historisées",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        annulationDispatching(lotA);
      },
    });
  };

  // Conserve ta logique pour afficher des sliders par groupes continus
  const groupByContinuity = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return [];
    const groups = [];
    let currentGroup = [arr[0]];
    for (let i = 1; i < arr.length; i++) {
      if (arr[i] === arr[i - 1] + 1) currentGroup.push(arr[i]);
      else {
        groups.push(currentGroup);
        currentGroup = [arr[i]];
      }
    }
    if (currentGroup.length > 0) groups.push(currentGroup);
    return groups;
  };

  const renderSliders = (groups, first, last, onChange) => {
    return groups.map((group, index) => (
      <div key={index} className="mb-4">
        <label className="form-label">Plage {index + 1} des attestations</label>
        <Slider
          range
          min={group[0]}
          max={group[group.length - 1]}
          defaultValue={[group[0], group[group.length - 1]]}
          value={[
            Math.max(first, group[0]),
            Math.min(last, group[group.length - 1]),
          ]}
          onChange={(value) => onChange(index, value)}
          styles={{
            track: {
              background: 'linear-gradient(to right, #32cd32, #32cd32)',
              height: 8,
              opacity: 0.8,
            },
            rail: {
              background: 'linear-gradient(to right, #1e90ff, #1e90ff)',
              height: 8,
              opacity: 0.3,
            },
            handle: {
              backgroundColor: '#32cd32',
              borderColor: '#32cd32',
              color: 'blueviolet',
              height: 8,
            }
          }}
        />
        <div className="d-flex justify-content-between">
          <span>Première: {group[0]}</span>
          <span>Dernière: {group[group.length - 1]}</span>
        </div>
      </div>
    ));
  };

  const validationReception = (lotA) => {
    setGetLotLoading(true);
    axiosInstance
      .get(`/api/dispatched-lot-attestation/reception/${lotA}`)
      .then(() => {
        window.location.reload();
        setGetLotLoading(false);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const annulationDispatching = (lotA) => {
    setGetLotLoading(true);
    axiosInstance
      .put(`/api/dispatched-lot-attestation/lot/dispatch/${lotA}/cancel`)
      .then(() => {
        window.location.reload();
        setGetLotLoading(false);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  // ── Transfert d'un dispatch vers une autre agence (numéros DISPONIBLES uniquement) ──
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferSource, setTransferSource] = useState(null); // le dispatch `l` source
  const [transferRecepteur, setTransferRecepteur] = useState("");
  const [transferAll, setTransferAll] = useState(true);       // tout le disponible (défaut) vs sous-plage
  const [transferFirst, setTransferFirst] = useState(0);
  const [transferLast, setTransferLast] = useState(0);
  const [transferLoading, setTransferLoading] = useState(false);

  // Droit de transférer : même garde que le dispatch (HLA062), hors Pool, sur sa propre compagnie.
  const canTransfer = !isPool
    && rightsService("HLA062")
    && lotAttestation?.compagnie?.code === LOGGED_USER?.profil?.organisme?.code;

  const openTransfer = (l) => {
    const avail = l.updatedLot || [];
    setTransferSource(l);
    setTransferAll(true);
    setTransferFirst(avail[0] || 0);
    setTransferLast(avail[avail.length - 1] || 0);
    // Destination par défaut : première agence/courtier différent(e) de la source.
    const firstOther = [...(agences || []), ...(courtiers || [])]
      .find((a) => a.codeAgence !== l.agence?.codeAgence);
    setTransferRecepteur(firstOther?.codeAgence || "");
    setTransferModalOpen(true);
  };

  const closeTransfer = () => {
    setTransferModalOpen(false);
    setTransferSource(null);
    setTransferLoading(false);
  };

  // Nom lisible d'une agence à partir de son codeAgence (agences + courtiers chargés).
  const agenceNameByCode = (code) =>
    [...(agences || []), ...(courtiers || [])].find((a) => a.codeAgence === code)?.nom || code;

  // Appel effectif du transfert (après confirmation).
  const doTransfer = (lot) => {
    setTransferLoading(true);
    axiosInstance
      .put(
        `/api/dispatched-lot-attestation/${transferSource.codeId}/transfer`,
        JSON.stringify({ recepteurCodeAgence: transferRecepteur, lot }),
        { headers: { "Content-Type": "application/json" } }
      )
      .then((res) => {
        toast("Transfert effectué avec succès.");
        closeTransfer();
        // La création du dispatch HISTORISE le lot (nouveau code_id, ancien désactivé) : on
        // redirige sur le codeId du LOT ACTIF renvoyé par le backend, sinon la page rechargée
        // sur l'ancien code_id (devenu inactif) revient vide.
        const lotCode = res?.data?.lotCodeId || lotAttestation?.codeId;
        if (lotCode) {
          window.location.href = `/dispatching-lot-attestation/${lotCode}`;
        } else {
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error(err);
        toast(err?.response?.data?.message || "Erreur lors du transfert.");
        setTransferLoading(false);
      });
  };

  const onTransfer = () => {
    if (!transferSource || !transferRecepteur) {
      toast("Sélectionnez l'agence destination.");
      return;
    }
    if (transferRecepteur === transferSource.agence?.codeAgence) {
      toast("L'agence destination doit être différente de la source.");
      return;
    }
    let lot = null; // null ⇒ tout le disponible (le backend prend updatedLot de la source)
    if (!transferAll) {
      if (!transferFirst || !transferLast || transferLast < transferFirst) {
        toast("Plage à transférer invalide.");
        return;
      }
      lot = Array.from({ length: transferLast - transferFirst + 1 }, (_, i) => transferFirst + i);
      const availSet = new Set(transferSource.updatedLot || []);
      if (lot.some((n) => !availSet.has(n))) {
        toast("La plage contient des numéros non disponibles (déjà utilisés ou hors du dispatch).");
        return;
      }
    }

    // Récap pour la confirmation.
    const count = lot ? lot.length : (transferSource.updatedLot?.length || 0);
    const plage = lot
      ? `${lot[0]} - ${lot[lot.length - 1]}`
      : `${transferSource.updatedLot?.[0]} - ${transferSource.updatedLot?.[transferSource.updatedLot.length - 1]}`;
    const sourceNom = transferSource.agence?.nom || "—";
    const destNom = agenceNameByCode(transferRecepteur);

    confirm({
      title: "Confirmer le transfert du dispatch ?",
      icon: <ExclamationCircleFilled />,
      okText: "Transférer",
      okType: "danger",
      cancelText: "Annuler",
      zIndex: 2000, // au-dessus de la modale de transfert (zIndex 1900)
      content: (
        <div>
          <p className="mb-2">Vous êtes sur le point de transférer :</p>
          <ul className="mb-2" style={{ paddingLeft: 18 }}>
            <li><strong>{count}</strong> attestation(s){transferAll ? " (tout le disponible)" : ""}</li>
            <li>Plage : <strong>{plage}</strong></li>
            <li>De : <strong>{sourceNom}</strong></li>
            <li>Vers : <strong>{destNom}</strong></li>
          </ul>
          <p className="text-muted mb-0" style={{ fontSize: 12 }}>
            Seuls les numéros disponibles sont déplacés ; les utilisés restent à l'agence source.
            Toutes vos actions sont historisées.
          </p>
        </div>
      ),
      onOk() {
        doTransfer(lot);
      },
    });
  };

  const transferBtn = (l) => (canTransfer && (l.updatedLot?.length > 0) ? (
    <button
      type="button"
      className="btn btn-outline-primary"
      onClick={() => openTransfer(l)}
    >
      Transférer
    </button>
  ) : null);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <div className="row g-6 mb-6">
                <div className="col-lg-12 col-xxl-12">
                  <div className="card h-100">
                    <div className="card-header d-flex align-items-center justify-content-between">
                      <h5 className="m-0 me-2">
                        Dispatching du lot d'attestation {lotAttestation?.typeAttestation?.typeAttestation || ""} de{" "}
                        <span style={{ color: "yellowgreen" }}>
                          {lotAttestation?.lot?.[0]} - {lotAttestation?.lot?.[lotAttestation?.lot?.length - 1]}
                        </span>{" "}
                        de {compagnie?.raisonSociale}
                      </h5>
                      {!isPool
                        && rightsService("HLA062")
                        && lotAttestation?.compagnie?.code === LOGGED_USER?.profil?.organisme?.code
                        && lotAttestation?.updatedLot?.length !== 0 && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setAffectationModalOpen(true)}
                          >
                            Effectuer un dispatch
                          </button>
                        )}
                    </div>
                    <Spin spinning={getLotLoading}>
                      <div className="card-body">
                        <ul className="p-0 m-0">
                          {lots?.length ? lots?.map((l, id) => (
                            <Card
                              key={id}
                              className="mb-4"
                              hoverable
                              bordered
                              bodyStyle={{ padding: '16px' }}
                              style={{
                                borderRadius: '8px',
                                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <Progress
                                  type="dashboard"
                                  percent={l.lot?.length
                                    ? Number((100 * ((l.lot.length - (l.updatedLot.length + (l.damagedLot?.length || 0))) / l.lot.length)).toFixed(1))
                                    : 0}
                                  width={60}
                                  style={{ marginRight: '16px' }}
                                />
                                <Descriptions column={1} style={{ flex: 1 }}>
                                  <Descriptions.Item label="Plage d'Attestations">
                                    <strong className="text-success">
                                      {l.lot[0]} - {l.lot[l.lot.length - 1]}
                                    </strong>
                                  </Descriptions.Item>
                                  <Descriptions.Item label="Statut">
                                    {l.dateReception ? (
                                      <>Livré le {frenchDate(l.dateReception)} à <u>{` ${l.agence.nom}`}</u></>
                                    ) : (
                                      <span className="text-warning">Pas encore livré</span>
                                    )}
                                  </Descriptions.Item>
                                </Descriptions>
                                <div className="text-end">
                                  {l.dateReception ? (
                                    // Suivi d'usage + transfert du reliquat disponible vers une autre agence.
                                    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                                      <span className="badge bg-label-secondary">
                                        {(l.lot.length - (l.updatedLot.length + (l.damagedLot?.length || 0)))}/{l.lot.length}
                                      </span>
                                      {transferBtn(l)}
                                    </span>
                                  ) : isPool ? (
                                    // Lecture seule pour le Pool : pas d'action, juste le statut
                                    <span className="text-warning">Pas encore livré</span>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => showReceptionAttestationConfirm(l.codeId)}
                                      >
                                        Valider réception
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={() => showAnnulationAttestationConfirm(l.codeId)}
                                      >
                                        Annuler dispatch
                                      </button>
                                      {transferBtn(l)}
                                    </>
                                  )}
                                </div>
                              </div>
                            </Card>
                          )) : (
                            <Empty
                              description="Aucun dispatching effectué pour l'instant"
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                          )}
                        </ul>
                      </div>
                    </Spin>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <Modal
        open={affectationModalOpen}
        loading={loading}
        onOk={onSubmit}
        confirmLoading={loading}
        okButtonProps={{ disabled: !isValid || loading }}
        onCancel={() => {
          setFirstAttestation(0);
          setLastAttestation(0);
          setNombre(0);
          setErrors({ first: '', range: '' });
          setAffectationModalOpen(false);
        }}
        zIndex={1900}
        centered
      >
        <div className="text-center mb-6">
          <h4 className="address-title mb-2">Nouveau Dispatch d'attestation</h4>
          <p className="address-subtitle">
            Affecter un nouveau lot d'attestation à une agence
          </p>
        </div>

        <form id="addNewAddressForm" className="row g-5">
          <div className="col-12">
            <label className="form-label">Veuillez sélectionner l'agence</label>
            <select
              className="form-control"
              value={selectedAgence}
              onChange={(e) => setSelectedAgence(e.target.value)}
            >
              <optgroup label="Agences">
                {agences
                  ?.filter((ag) => ag.typeAgence !== "COURTIER")
                  .map((ag, id) => (
                    <option key={id} value={ag.codeAgence}>{ag.nom}</option>
                  ))}
              </optgroup>
              <optgroup label="Courtiers">
                {courtiers
                  ?.filter((ag) => ag.typeAgence === "COURTIER")
                  .map((ag, id) => (
                    <option key={id} value={ag.codeAgence}>{ag.nom}</option>
                  ))}
              </optgroup>
            </select>
          </div>

          <div className="col-12">
            {renderSliders(
              groupByContinuity(lotAttestation?.updatedLot || []),
              firstAttestation,
              lastAttestation,
              (index, value) => {
                setFirstAttestation(value[0]);
                setLastAttestation(value[1]);
                setNombre(Math.max(1, value[1] - value[0] + 1));
                validateRange(value[0], value[1], null);
              }
            )}
          </div>

          <div className="col-12">
            <label className="form-label">Premier numéro à assigner</label>
            <Input
              type="number"
              value={firstAttestation}
              min={lotAttestation?.updatedLot?.[0] ?? 0}
              max={lotAttestation?.updatedLot?.[lotAttestation?.updatedLot?.length - 1] ?? 0}
              status={errors.first ? 'error' : ''}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                setFirstAttestation(value);
                setLastAttestation(value);
                if (nombre > 0) setNombre(1);
                const ok = validateFirst(value);
                if (ok) setErrors((er) => ({ ...er, range: '' }));
              }}
            />
            {errors.first && <div className="text-danger mt-1">{errors.first}</div>}
          </div>

          <div className="col-12">
            <label className="form-label">Nombre</label>
            <Input
              type="number"
              value={nombre}
              min={1}
              onChange={(e) => {
                let value = parseInt(e.target.value, 10);
                if (!value || value < 1) value = 1;
                setNombre(value);

                const g = findGroupFor(firstAttestation);
                if (g) {
                  const desiredLast = firstAttestation + value - 1;
                  const cappedLast = Math.min(desiredLast, g[g.length - 1]);
                  setLastAttestation(cappedLast);
                  if (desiredLast > cappedLast) {
                    setErrors((er) => ({
                      ...er,
                      range: `Plage réduite à ${firstAttestation} - ${cappedLast} (rupture).`,
                    }));
                  } else {
                    setErrors((er) => ({ ...er, range: "" }));
                  }
                }

                validateRange(firstAttestation, firstAttestation + value - 1, value);
              }}
            />
          </div>

          <div className="col-12">
            <label className="form-label">Dernier numéro à assigner</label>
            <Input
              type="number"
              value={lastAttestation}
              min={firstAttestation}
              max={lotAttestation?.updatedLot?.[lotAttestation?.updatedLot?.length - 1] ?? 0}
              status={errors.range ? 'error' : ''}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                setLastAttestation(value);
                setNombre(Math.max(1, value - firstAttestation + 1));
                validateRange(firstAttestation, value, null);
              }}
            />
            {errors.range && <div className="text-danger mt-1">{errors.range}</div>}
          </div>
        </form>
      </Modal>

      {/* ── Modale de TRANSFERT d'un dispatch vers une autre agence ── */}
      <Modal
        open={transferModalOpen}
        onOk={onTransfer}
        confirmLoading={transferLoading}
        okText="Transférer"
        okButtonProps={{ disabled: transferLoading || !transferRecepteur }}
        onCancel={closeTransfer}
        zIndex={1900}
        centered
      >
        <div className="text-center mb-6">
          <h4 className="address-title mb-2">Transférer le dispatch</h4>
          <p className="address-subtitle">
            Déplacer les attestations <strong>disponibles</strong> vers une autre agence
            {transferSource?.agence?.nom ? <> (source : <u>{transferSource.agence.nom}</u>)</> : null}
          </p>
        </div>

        <form className="row g-5">
          <div className="col-12">
            <label className="form-label">Agence destination</label>
            <select
              className="form-control"
              value={transferRecepteur}
              onChange={(e) => setTransferRecepteur(e.target.value)}
            >
              <option value="" disabled>Sélectionner une agence…</option>
              <optgroup label="Agences">
                {agences
                  ?.filter((ag) => ag.typeAgence !== "COURTIER" && ag.codeAgence !== transferSource?.agence?.codeAgence)
                  .map((ag, id) => (
                    <option key={id} value={ag.codeAgence}>{ag.nom}</option>
                  ))}
              </optgroup>
              <optgroup label="Courtiers">
                {courtiers
                  ?.filter((ag) => ag.typeAgence === "COURTIER" && ag.codeAgence !== transferSource?.agence?.codeAgence)
                  .map((ag, id) => (
                    <option key={id} value={ag.codeAgence}>{ag.nom}</option>
                  ))}
              </optgroup>
            </select>
          </div>

          <div className="col-12 d-flex align-items-center" style={{ gap: 10 }}>
            <Switch checked={transferAll} onChange={setTransferAll} />
            <span>
              Transférer <strong>tout le disponible</strong>
              {transferSource ? <> ({transferSource.updatedLot?.length || 0} numéro(s))</> : null}
            </span>
          </div>

          {!transferAll && (
            <>
              <div className="col-12">
                {renderSliders(
                  groupByContinuity(transferSource?.updatedLot || []),
                  transferFirst,
                  transferLast,
                  (index, value) => {
                    setTransferFirst(value[0]);
                    setTransferLast(value[1]);
                  }
                )}
              </div>
              <div className="col-12 text-muted">
                Plage à transférer : <strong>{transferFirst} - {transferLast}</strong>
                {" "}({Math.max(0, transferLast - transferFirst + 1)} numéro(s))
              </div>
            </>
          )}
        </form>
      </Modal>
    </div>
  );
};

export default DetailsLotAttestationsDispatched;
